import { useTranslation } from 'react-i18next';
import { Plus, FolderPlus, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { AsyncCombobox } from '@/shared/components/custom/async-combobox/AsyncCombobox';
import type { AsyncComboboxOption } from '@/shared/components/custom/async-combobox/AsyncCombobox.types';
import { useProjectMembers } from '@/features/projects/public';
import { useBoards, useBoardStatuses, useBoardGroups } from '@/features/boards/public';
import { useTags } from '@/features/tags/public';
import type {
  WidgetFilterField,
  WidgetFilterGroup,
  WidgetFilterOperator,
  WidgetFilterRule,
} from '../../types';

const FIELDS: WidgetFilterField[] = [
  'status',
  'assignee',
  'priority',
  'group',
  'board',
  'tag',
  'title',
  'dueDate',
  'startDate',
];

type FieldType = 'text' | 'select' | 'date' | 'number' | 'user';

const FIELD_TYPE: Record<WidgetFilterField, FieldType> = {
  title: 'text',
  status: 'select',
  group: 'select',
  board: 'select',
  tag: 'select',
  assignee: 'user',
  priority: 'number',
  dueDate: 'date',
  startDate: 'date',
};

// Operators allowed per field type — mirrors the backend FilterEngine.
const OPS_BY_TYPE: Record<FieldType, WidgetFilterOperator[]> = {
  text: ['eq', 'neq', 'contains', 'is_empty', 'is_not_empty'],
  select: ['eq', 'neq', 'in', 'nin', 'is_empty', 'is_not_empty'],
  user: ['eq', 'neq', 'in', 'nin', 'is_empty', 'is_not_empty'],
  number: ['eq', 'neq', 'in', 'nin', 'gt', 'gte', 'lt', 'lte'],
  date: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'is_empty', 'is_not_empty'],
};

const PRIORITIES = [0, 1, 2, 3, 4];
const NO_VALUE_OPS: WidgetFilterOperator[] = ['is_empty', 'is_not_empty'];
const ARRAY_OPS: WidgetFilterOperator[] = ['in', 'nin'];
const MAX_DEPTH = 2;

function isGroup(node: WidgetFilterRule | WidgetFilterGroup): node is WidgetFilterGroup {
  return 'rules' in node;
}

function valueToText(value: WidgetFilterRule['value']): string {
  if (Array.isArray(value)) return value.join(', ');
  return value === undefined || value === null ? '' : String(value);
}

// Fields whose value is a Snowflake ID resolved from another module — these
// must never be free text (see the backend bug where `value: "Todo"` silently
// matched everything instead of failing, because filtersJson expects a numeric
// StatusId, not a status name).
const ID_LOOKUP_FIELDS = new Set<WidgetFilterField>([
  'status',
  'assignee',
  'group',
  'board',
  'tag',
]);

interface FilterRuleRowProps {
  rule: WidgetFilterRule;
  onChange: (rule: WidgetFilterRule) => void;
  onRemove: () => void;
  projectId?: string;
  boardId?: string;
}

function useIdLookupOptions(
  field: WidgetFilterField,
  projectId?: string,
  boardId?: string,
): { options: AsyncComboboxOption[]; needsProject: boolean; needsBoard: boolean } {
  // Hooks must run unconditionally regardless of `field` (rules of hooks) —
  // each is a no-op query when its id is missing.
  const members = useProjectMembers(projectId ?? '');
  const boards = useBoards(projectId ?? '');
  const tags = useTags(projectId ?? null);
  const statuses = useBoardStatuses(boardId ?? '');
  const groups = useBoardGroups(boardId ?? '');

  switch (field) {
    case 'assignee':
      return {
        needsProject: true,
        needsBoard: false,
        options: (members.data ?? []).map((m) => ({
          id: m.userId,
          label: m.displayName || m.email,
        })),
      };
    case 'board':
      return {
        needsProject: true,
        needsBoard: false,
        options: boards.boards.flatMap((b) => (b.id ? [{ id: b.id, label: b.name ?? '' }] : [])),
      };
    case 'tag':
      return {
        needsProject: true,
        needsBoard: false,
        options: (tags.data?.items ?? []).flatMap((tg) =>
          tg.id ? [{ id: tg.id, label: tg.name ?? '' }] : [],
        ),
      };
    case 'status':
      return {
        needsProject: false,
        needsBoard: true,
        options: statuses.statuses.flatMap((s) =>
          s.id ? [{ id: s.id, label: s.name ?? '' }] : [],
        ),
      };
    case 'group':
      return {
        needsProject: false,
        needsBoard: true,
        options: groups.groups.map((g) => ({ id: g.id, label: g.name })),
      };
    default:
      return { needsProject: false, needsBoard: false, options: [] };
  }
}

function FilterRuleRow({ rule, onChange, onRemove, projectId, boardId }: FilterRuleRowProps) {
  const { t } = useTranslation('dashboard');
  const fieldType = FIELD_TYPE[rule.field];
  const ops = OPS_BY_TYPE[fieldType];
  const needsValue = !NO_VALUE_OPS.includes(rule.operator);
  const isArray = ARRAY_OPS.includes(rule.operator);
  const isIdLookup = ID_LOOKUP_FIELDS.has(rule.field);
  const {
    options: idOptions,
    needsProject,
    needsBoard,
  } = useIdLookupOptions(rule.field, projectId, boardId);
  const idPickerDisabled = (needsProject && !projectId) || (needsBoard && !boardId);

  const handleFieldChange = (field: WidgetFilterField) => {
    const nextOps = OPS_BY_TYPE[FIELD_TYPE[field]];
    onChange({ field, operator: nextOps[0], value: '' });
  };

  const handleOperatorChange = (operator: WidgetFilterOperator) => {
    const next: WidgetFilterRule = { field: rule.field, operator };
    if (!NO_VALUE_OPS.includes(operator)) {
      next.value = ARRAY_OPS.includes(operator) ? [] : '';
    }
    onChange(next);
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="dashboard-filter-rule">
      <Select value={rule.field} onValueChange={(v) => handleFieldChange(v as WidgetFilterField)}>
        <SelectTrigger className="h-8 w-[130px]" data-testid="dashboard-filter-field">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {FIELDS.map((f) => (
            <SelectItem key={f} value={f}>
              {t(`filters.fields.${f}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={rule.operator}
        onValueChange={(v) => handleOperatorChange(v as WidgetFilterOperator)}
      >
        <SelectTrigger className="h-8 w-[130px]" data-testid="dashboard-filter-operator">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ops.map((op) => (
            <SelectItem key={op} value={op}>
              {t(`filters.operators.${op}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {needsValue && fieldType === 'number' && !isArray && (
        <Select
          value={valueToText(rule.value)}
          onValueChange={(v) => onChange({ ...rule, value: Number(v) })}
        >
          <SelectTrigger className="h-8 w-[130px]" data-testid="dashboard-filter-value">
            <SelectValue placeholder={t('filters.valuePlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {PRIORITIES.map((p) => (
              <SelectItem key={p} value={String(p)}>
                {t(`filters.priorities.${p}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {needsValue && fieldType === 'number' && isArray && (
        <AsyncCombobox
          mode="multi"
          staticOptions={PRIORITIES.map((p) => ({
            id: String(p),
            label: t(`filters.priorities.${p}`),
          }))}
          queryKey={['dashboard-filter-priorities']}
          value={(Array.isArray(rule.value) ? rule.value : []).map(String)}
          onChange={(ids) => onChange({ ...rule, value: ids.map(Number) })}
          placeholder={t('filters.valuePlaceholder')}
          triggerClassName="h-8 w-[150px]"
          data-testid="dashboard-filter-value"
        />
      )}

      {needsValue && fieldType === 'date' && (
        <Input
          type="date"
          className="h-8 w-[150px]"
          value={valueToText(rule.value)}
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
          data-testid="dashboard-filter-value"
        />
      )}

      {needsValue && isIdLookup && (
        <AsyncCombobox
          {...(isArray
            ? {
                mode: 'multi' as const,
                value: Array.isArray(rule.value) ? rule.value.map(String) : [],
                onChange: (ids: string[]) => onChange({ ...rule, value: ids }),
              }
            : {
                mode: 'single' as const,
                value: (rule.value as string) || null,
                onChange: (id: string | null) => onChange({ ...rule, value: id ?? '' }),
                clearable: true,
              })}
          staticOptions={idOptions}
          queryKey={['dashboard-filter-options', rule.field, projectId, boardId]}
          disabled={idPickerDisabled}
          placeholder={
            idPickerDisabled
              ? t(needsBoard ? 'filters.selectBoardFirst' : 'filters.selectProjectFirst')
              : t('filters.valuePlaceholder')
          }
          triggerClassName="h-8 w-[180px]"
          data-testid="dashboard-filter-value"
        />
      )}

      {needsValue && fieldType === 'text' && (
        <Input
          className="h-8 w-[150px]"
          placeholder={t('filters.valuePlaceholder')}
          value={valueToText(rule.value)}
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
          data-testid="dashboard-filter-value"
        />
      )}

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={onRemove}
            aria-label={t('filters.remove')}
            data-testid="dashboard-filter-remove-rule"
          >
            <X className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('filters.remove')}</TooltipContent>
      </Tooltip>
    </div>
  );
}

interface FilterBuilderProps {
  value?: WidgetFilterGroup;
  onChange: (group: WidgetFilterGroup) => void;
  depth?: number;
  /** Owning project — resolves options for the assignee/board/tag filters. */
  projectId?: string;
  /** Owning board — resolves options for the status/group filters. */
  boardId?: string;
}

export function FilterBuilder({
  value,
  onChange,
  depth = 0,
  projectId,
  boardId,
}: FilterBuilderProps) {
  const { t } = useTranslation('dashboard');
  const group: WidgetFilterGroup = value ?? { operator: 'AND', rules: [] };

  const updateRuleAt = (index: number, node: WidgetFilterRule | WidgetFilterGroup) =>
    onChange({ ...group, rules: group.rules.map((r, i) => (i === index ? node : r)) });

  const removeRuleAt = (index: number) =>
    onChange({ ...group, rules: group.rules.filter((_, i) => i !== index) });

  // `status` needs a board to resolve its options, so it is only a sensible
  // starting point when one is already pinned — otherwise the rule would land
  // with a disabled, unfillable value picker.
  const addRule = () =>
    onChange({
      ...group,
      rules: [
        ...group.rules,
        boardId
          ? { field: 'status', operator: 'eq', value: '' }
          : { field: 'priority', operator: 'eq', value: '' },
      ],
    });

  const addGroup = () =>
    onChange({ ...group, rules: [...group.rules, { operator: 'AND', rules: [] }] });

  return (
    <div className="space-y-2 rounded-md border p-3" data-testid="dashboard-filter-group">
      <div className="flex items-center gap-2">
        <Select
          value={group.operator}
          onValueChange={(v) => onChange({ ...group, operator: v as 'AND' | 'OR' })}
        >
          <SelectTrigger className="h-8 w-[160px]" data-testid="dashboard-filter-logic">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="AND">{t('filters.matchAll')}</SelectItem>
            <SelectItem value="OR">{t('filters.matchAny')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {group.rules.map((node, index) =>
        isGroup(node) ? (
          <div key={index} className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <FilterBuilder
                value={node}
                onChange={(g) => updateRuleAt(index, g)}
                depth={depth + 1}
                projectId={projectId}
                boardId={boardId}
              />
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={() => removeRuleAt(index)}
                  aria-label={t('filters.remove')}
                >
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('filters.remove')}</TooltipContent>
            </Tooltip>
          </div>
        ) : (
          <FilterRuleRow
            key={index}
            rule={node}
            onChange={(r) => updateRuleAt(index, r)}
            onRemove={() => removeRuleAt(index)}
            projectId={projectId}
            boardId={boardId}
          />
        ),
      )}

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addRule}
          data-testid="dashboard-filter-add-rule"
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          {t('filters.addRule')}
        </Button>
        {depth < MAX_DEPTH && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addGroup}
            data-testid="dashboard-filter-add-group"
          >
            <FolderPlus className="mr-1 h-3.5 w-3.5" />
            {t('filters.addGroup')}
          </Button>
        )}
      </div>
    </div>
  );
}
